// <s-product-gallery> — one-image-at-a-time product gallery.
// The [data-product-gallery] track is a native scroll-snap strip (swipe on touch);
// thumbnails and prev/next arrows scroll it, and the active thumbnail follows the scroll.
class SProductGallery extends HTMLElement {
  #ac = null;
  #index = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    const track = this.querySelector("[data-product-gallery]");
    if (!track) return;

    this.addEventListener("click", (event) => {
      const thumb = event.target.closest("[data-gallery-thumb]");
      if (thumb) return this.go(Number(thumb.dataset.galleryThumb));
      if (event.target.closest("[data-gallery-prev]")) return this.go(this.#index - 1);
      if (event.target.closest("[data-gallery-next]")) return this.go(this.#index + 1);
    }, { signal });

    let frame = 0;
    track.addEventListener("scroll", () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => this.#sync());
    }, { signal, passive: true });

    this.#sync();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  get #track() {
    return this.querySelector("[data-product-gallery]");
  }

  get #slides() {
    return [...this.querySelectorAll("[data-gallery-slide]")];
  }

  go(index) {
    const slides = this.#slides;
    if (!slides.length) return;
    const target = Math.max(0, Math.min(index, slides.length - 1));
    const track = this.#track;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    track.scrollTo({ left: slides[target].offsetLeft - track.offsetLeft, behavior: reduce ? "auto" : "smooth" });
    this.#setActive(target);
  }

  #sync() {
    const track = this.#track;
    const width = track.clientWidth || 1;
    const index = Math.round(Math.abs(track.scrollLeft) / width);
    this.#setActive(Math.min(index, this.#slides.length - 1));
  }

  #setActive(index) {
    this.#index = Math.max(0, index);
    const count = this.#slides.length;
    this.querySelectorAll("[data-gallery-thumb]").forEach((thumb) => {
      const active = Number(thumb.dataset.galleryThumb) === this.#index;
      if (active) thumb.setAttribute("aria-current", "true");
      else thumb.removeAttribute("aria-current");
    });
    const prev = this.querySelector("[data-gallery-prev]");
    const next = this.querySelector("[data-gallery-next]");
    if (prev) prev.disabled = this.#index === 0;
    if (next) next.disabled = this.#index >= count - 1;
  }
}

if (!customElements.get("s-product-gallery")) customElements.define("s-product-gallery", SProductGallery);
